import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppContext } from '@prisjakt/ui-app-context';
import PopOver from '@prisjakt/ui-popover';
import RatingHelper from '@prisjakt/ui-rating/src/rating-helper';
import AlternativePriceRow from './components/alternative-price-row';
import ExpandButton from './components/expand-button';
import ExpandedInfo from './components/expanded-info';
import GoToStoreButton from './components/go-to-store-button';
import PriceColumn from './components/price-column';
import ReportButton from './components/report-button';
import StoreInfoColumn from './components/store-info-column';

import {
  Award,
  Campaign,
  Price,
  ProductMediaFragment,
  ProductPageFragment,
} from '../../types';
import { SortOption } from '../../areas/product/components/sort-options/types';
import { CurrentCampaign, ProductPriceType } from '../../areas/product/types';

import { trackExpandPriceRowClick } from './tracking';

import { fetchSotyAwardsByStoreId } from './requests';

import * as S from './styles';
import * as Table from './styles/table';

export const columnCount = 6;

enum ReportRowType {
  NONE = 'none',
  DEFAULT = 'default',
  ALTERNATIVE = 'alternative',
}

type OwnProps = {
  alternativePrices: Price['alternativePrices'];
  brand?: ProductPageFragment['brand'] | null;
  brandRecommendedRetailer: boolean;
  campaign: CurrentCampaign | null;
  currentSort: SortOption;
  deliveryOptions: Price['deliveryOptions'];
  externalUri?: string | null;
  isInternationalPrice: boolean;
  media: ProductMediaFragment['media'] & { first?: string | null };
  name: Price['name'];
  onClickOutBounce: (id: number, priceId?: string | null) => void;
  onPriceClick: (
    featured: boolean,
    price: number | undefined,
    status: string,
    isAlternative: boolean
  ) => void;
  openInNewWindow: boolean;
  price: Price['price'];
  priceId?: string | null;
  priceType?: ProductPriceType | null;
  productId: number;
  productName?: string;
  stock: Price['stock'];
  store: Price['store'];
  storeInfo?: (string | null)[] | null;
};

const PriceRow = ({
  alternativePrices,
  brand,
  brandRecommendedRetailer,
  campaign,
  currentSort,
  deliveryOptions = [],
  externalUri,
  isInternationalPrice,
  media,
  name,
  onClickOutBounce,
  onPriceClick,
  openInNewWindow,
  price,
  priceId,
  priceType,
  productId,
  productName,
  stock,
  store,
  storeInfo,
}: OwnProps) => {
  const [showDisabledInfo, setShowDisabledInfo] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [priceRowReport, setPriceRowReport] = useState(ReportRowType.NONE);
  const [sotyBadge, setSotyBadge] = useState<Award | undefined | null>();
  const [reportRowId, setReportRowId] = useState<string | null>(null);
  const priceRow = useRef<HTMLTableRowElement>(null);
  const { t } = useAppContext();

  const featured = Boolean(store?.featured);

  const rating = store?.userReviewSummary?.rating
    ? RatingHelper.convert(store.userReviewSummary.rating, 1)
    : undefined;

  const handleOutSideClick = useCallback(
    e => {
      if (priceRow && priceRow.current && priceRow.current.contains(e.target)) {
        return;
      }
      setShowDisabledInfo(false);
    },
    [priceRow]
  );

  const startBounceTimer = useCallback(
    (id: string) => {
      // Start a timer when a user clicks out on a product
      // if they return to Prisjakt within 15 seconds
      // display an error report button in the price row.

      const startTime = Date.now();

      const onRefocus = () => {
        if (
          Date.now() - startTime > 15000 &&
          document.visibilityState === 'visible'
        ) {
          document.removeEventListener('visibilitychange', onRefocus);
          return;
        }

        if (document.visibilityState === 'visible') {
          setReportRowId(id);

          // Action event for tracking
          onClickOutBounce(productId, priceId);

          document.removeEventListener('visibilitychange', onRefocus);
        }
      };

      document.addEventListener('visibilitychange', onRefocus);
    },
    [onClickOutBounce, priceId, productId, setReportRowId]
  );

  // componentWillUnmount
  useEffect(() => {
    return () => {
      window.removeEventListener('click', handleOutSideClick);
    };
  }, [handleOutSideClick]);

  // Callback for showDisabledInfo state change
  useEffect(() => {
    if (showDisabledInfo) {
      window.addEventListener('click', handleOutSideClick);
    } else {
      window.removeEventListener('click', handleOutSideClick);
    }
  }, [handleOutSideClick, showDisabledInfo]);

  const toggleDisabledInfo = useCallback(() => {
    setShowDisabledInfo(!showDisabledInfo);
  }, [showDisabledInfo]);

  const handleClickOut = useCallback(
    (e, id, isAlternative = false) => {
      if (externalUri) {
        if (IS_BROWSER) {
          startBounceTimer(id);
        }
        if (!showDisabledInfo && !e.defaultPrevented) {
          onPriceClick(
            Boolean(store && store.featured),
            undefined,
            isInternationalPrice ? 'international' : '',
            isAlternative
          );
        }
      } else {
        e.preventDefault();
        toggleDisabledInfo();
      }
    },
    [
      externalUri,
      isInternationalPrice,
      onPriceClick,
      showDisabledInfo,
      startBounceTimer,
      store,
      toggleDisabledInfo,
    ]
  );

  const onClickOut = useCallback(
    e => {
      setPriceRowReport(ReportRowType.DEFAULT);
      handleClickOut(e, priceId);
    },
    [handleClickOut, priceId]
  );

  const tableLinkProps = {
    rel: 'nofollow noopener',
    href: externalUri || undefined,
    onClick: onClickOut,
    target: openInNewWindow ? '_blank' : '',
    disabled: !externalUri,
  };

  const handleExpandButtonClick = () => {
    setExpanded(!expanded);
    if (!expanded) {
      trackExpandPriceRowClick(productId, productName, store?.id, store?.name);

      if (sotyBadge === undefined && store?.id) {
        fetchSotyAwardsByStoreId(store.id).then(response => {
          if (response?.data?.store?.storeOfTheYearAwards?.length) {
            setSotyBadge(response.data.store.storeOfTheYearAwards[0]);
          } else {
            setSotyBadge(null);
          }
        });
      }
    }
  };

  const handleClickOutAltPrice = useCallback(
    (e, id) => {
      setPriceRowReport(ReportRowType.ALTERNATIVE);
      handleClickOut(e, id, true);
    },
    [handleClickOut]
  );

  const closeReportOverlay = useCallback(() => {
    setPriceRowReport(ReportRowType.NONE);
    setReportRowId(null);
  }, [setReportRowId]);

  const renderExpandButtonColumn = () => (
    <Table.Column>
      <ExpandButton
        expanded={expanded}
        onClick={handleExpandButtonClick}
        title={t('product', 'price.more-info')}
      />
    </Table.Column>
  );

  const renderStoreInfoColumn = () => (
    <Table.Column>
      <Table.Link alignLeft {...tableLinkProps}>
        {store && (
          <StoreInfoColumn store={store} hide={expanded} rating={rating} />
        )}
      </Table.Link>
    </Table.Column>
  );

  const renderProductNameColumn = () => (
    <Table.Column expand>
      <Table.Link alignLeft {...tableLinkProps}>
        <S.ProductNameColumn>
          <S.ProductName>{name}</S.ProductName>

          {brandRecommendedRetailer && (
            <S.RecommendedRetailer>
              <S.RecommendedRetailerIcon />
              {t('store', 'brand.recommended-retailer')}
            </S.RecommendedRetailer>
          )}
        </S.ProductNameColumn>
      </Table.Link>
    </Table.Column>
  );

  const renderStockStatus = () => (
    <Table.Column>
      <Table.Link alignCenter {...tableLinkProps}>
        <S.StockStatusColumn
          status={stock && stock.status}
          statusText={stock && stock.statusText}
          deliveryOptions={deliveryOptions}
          countryCode={store && store.countryCode}
          toneDown={!featured}
          isInternationalPrice={isInternationalPrice}
          showTooltip
          priceType={priceType}
        />
      </Table.Link>
    </Table.Column>
  );

  const renderPrice = () => {
    if (!price) return null;

    return (
      <PriceColumn
        price={price}
        storeIsFeatured={featured}
        isCampaign={Boolean(campaign)}
        align="right"
        currentSort={currentSort}
      />
    );
  };

  const renderPriceColumn = () => (
    <Table.Column>
      <Table.Link alignRight {...tableLinkProps}>
        {/* Only visible for baseline and desktop */}
        <S.PriceColumnWrapper>
          {campaign && (
            <S.CampaignBadge
              campaign={
                { ...campaign, id: parseInt(campaign.id, 10) } as Campaign
              }
            />
          )}
          {renderPrice()}
        </S.PriceColumnWrapper>
      </Table.Link>
    </Table.Column>
  );

  const renderGoToStoreButton = () => (
    <Table.Column>
      {externalUri ? (
        <Table.Link {...tableLinkProps}>
          <GoToStoreButton visible disabled={!featured} featured={featured} />
        </Table.Link>
      ) : (
        <PopOver
          id={`click-out-${priceId}`}
          open={Boolean(showDisabledInfo)}
          width={250}
          offsetY={-35}
          offsetX={40}
          arrowOffsetY={35}
          position="left"
          label={t('product', 'transparency.prices-more-info-button')}
          onClose={toggleDisabledInfo}
        >
          <Table.Link {...tableLinkProps}>
            <GoToStoreButton
              visible={Boolean(externalUri || featured)}
              disabled={Boolean(externalUri && !featured)}
              featured={featured}
            />
          </Table.Link>
          <S.TransparencyShopInfo>
            {t('product', 'transparency.store-info')}
          </S.TransparencyShopInfo>
        </PopOver>
      )}
    </Table.Column>
  );

  return (
    <React.Fragment key={store?.id || 'price-row'}>
      <Table.Row // Classname is used to track click out
        className="pj-ui-price-row"
        nonFeatured={!featured}
        noExternalUri={!externalUri}
        showReport={reportRowId === priceId}
        data-test="PriceRow"
        ref={priceRow}
        expanded={expanded}
      >
        {renderExpandButtonColumn()}
        {renderStoreInfoColumn()}
        {renderProductNameColumn()}
        {renderStockStatus()}
        {renderPriceColumn()}
        {renderGoToStoreButton()}
      </Table.Row>
      {expanded && store && (
        <Table.InfoRow data-test="ColumnExpanded">
          <td colSpan={columnCount}>
            <ExpandedInfo
              brand={brand}
              brandRecommendedRetailer={brandRecommendedRetailer}
              deliveryOptions={deliveryOptions}
              priceId={priceId}
              rating={rating}
              store={store}
              storeInfo={storeInfo}
              sotyBadge={sotyBadge}
            />
          </td>
        </Table.InfoRow>
      )}
      {expanded &&
        alternativePrices &&
        alternativePrices.map(altPrice => {
          if (!altPrice) return null;

          return (
            <React.Fragment key={altPrice.id || 'alternative-price-row'}>
              <AlternativePriceRow
                key={altPrice.id ? altPrice.id : 'alternative-price-row'}
                externalUri={externalUri}
                campaign={campaign}
                onClickOut={handleClickOutAltPrice}
                isInternationalPrice={isInternationalPrice}
                price={altPrice}
                priceStore={store}
                productImage={media.first}
                tableLinkProps={tableLinkProps}
                currentSort={currentSort}
              />
              {priceRowReport === ReportRowType.ALTERNATIVE &&
                reportRowId === altPrice.id && (
                  <Table.AltReportRow>
                    <Table.Column colSpan={columnCount}>
                      <ReportButton
                        priceStore={store}
                        priceId={altPrice.id}
                        priceValue={
                          altPrice.price && altPrice.price.exclShipping
                        }
                        stockStatus={altPrice.stock && altPrice.stock.status}
                        productImage={media.first}
                        onClose={closeReportOverlay}
                        productName={altPrice.name}
                      />
                    </Table.Column>
                  </Table.AltReportRow>
                )}
            </React.Fragment>
          );
        })}
      {priceRowReport === ReportRowType.DEFAULT && reportRowId === priceId && (
        <Table.ReportRow>
          <Table.Column colSpan={columnCount}>
            <ReportButton
              priceStore={store}
              priceId={priceId}
              priceValue={price && price.exclShipping}
              stockStatus={stock && stock.status}
              productImage={media.first}
              onClose={closeReportOverlay}
              productName={productName}
            />
          </Table.Column>
        </Table.ReportRow>
      )}
    </React.Fragment>
  );
};

export default PriceRow;
